import React, { Component } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import wirisDialogHider from './wiris-dialog-hider'; // Import the helper class
import stripNonLatin1 from '../../utils/sanitization/stripNonLatin1';
import { injectIntl } from 'react-intl';

/* ------------------------------------------------------------------
 * Self-hosted TinyMCE 6, bundled into the app (no Tiny Cloud, no API key).
 * Importing 'tinymce/tinymce' puts `tinymce` on the global scope, so the
 * React wrapper uses it instead of loading a script from anywhere.
 * ------------------------------------------------------------------ */
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';

// Editor UI skin (toolbar, dialogs, menus)
import 'tinymce/skins/ui/oxide/skin.min.css';

// Plugins used below; each one must be imported explicitly
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/code';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';

// MathType / Wiris for TinyMCE 6, bundled (must come after the tinymce imports).
// If this import fails to resolve, use the full file path instead, e.g.:
// import '@wiris/mathtype-tinymce6/plugin.min.js';
import '@wiris/mathtype-tinymce6';

// Styles applied inside the editable area (replaces the default content CSS,
// which can't be loaded from a URL when TinyMCE is bundled).
const CONTENT_STYLE = `
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.4; margin: 1rem; }
  table { border-collapse: collapse; }
  table td, table th { border: 1px solid #ccc; padding: 0.4rem; }
  table[data-mce-selected] td[data-mce-selected], table td[data-mce-selected] { background-color: #b4d7ff; }
  img { max-width: 100%; height: auto; }
  img[data-mce-selected], video[data-mce-selected], audio[data-mce-selected] { outline: 3px solid #b4d7ff; }
  .mce-object { border: 1px dashed #aaa; }
`;

class TinyMCE extends Component {

    constructor(props) {
        super(props);

        this.handleEditorChange = this.handleEditorChange.bind(this);
        this.state = {
            content: props.content || "",
            // flag to track if the change is internal (from typing) or external (from props)
            isInternalChange: false
        };
        this.editor = null;

        // Captured once. tinymce-react v4 resets the editor whenever initialValue
        // changes, so it must stay stable; later external updates are pushed in
        // componentDidUpdate instead.
        this.initialContent = props.content || "";
    }

    handleEditorChange = (content) => {
        const sanitizedContent = stripNonLatin1(content);

        this.setState(
            {
                content: sanitizedContent,
                isInternalChange: true
            },
            function () {
                this.props.onChange(this.props.parentStateKey, sanitizedContent);

                this.setState({ isInternalChange: false });
            }
        );
    };

    componentDidMount() {
        this.setState({ content: this.props.content || "" });
        if (typeof this.props.observer !== 'undefined' && this.props.observer === true) {
            wirisDialogHider.startObserving(); // Start the listener when component mounts
        }
    }

    componentWillUnmount() {
        if (typeof this.props.observer !== 'undefined' && this.props.observer === true) {
            wirisDialogHider.stopObserving(); // Stop the listener when component unmounts
        }
    }

    componentDidUpdate(prevProps) {
        // if content prop has changed from outside and the change is not internal
        // push the new HTML to the uncontrolled editor.
        if (prevProps.content !== this.props.content && !this.state.isInternalChange) {
            const newHtml = this.props.content || "";

            if (newHtml !== this.state.content && this.editor) {
                try {
                    const bookmark = this.editor.selection.getBookmark(2, true);
                    this.editor.setContent(newHtml);
                    this.editor.selection.moveToBookmark(bookmark);
                } catch (e) {
                    this.editor.setContent(newHtml);
                }
                // update the local mirror
                this.setState({ content: newHtml });
            }
        }
    }


    render() {
        const { intl } = this.props;

        return (
            <Editor
                // No apiKey and no tinymceScriptSrc: the bundled global `tinymce` is used.
                initialValue={this.initialContent}
                onInit={(_evt, editor) => { this.editor = editor; }}
                // value={this.props.content}
                init={{
                    // Bundled setup: skin CSS is imported above, content styles are inline
                    skin: false,
                    content_css: false,
                    content_style: CONTENT_STYLE,

                    promotion: false,   // hides the "Upgrade" button
                    branding: false,    // hides "Powered by TinyMCE"

                    // tiny_mce_wiris is bundled above, so it's a regular plugin (no external_plugins)
                    plugins: 'link image code media table lists tiny_mce_wiris',
                    toolbar: 'undo redo | indent bold italic underline forecolor table numlist bullist | alignleft aligncenter alignright | code | tiny_mce_wiris_formulaEditor addColonsButton addOpenColonButton addCloseColonButton ',
                    setup: function (editor) {
                        const errorText = intl.formatMessage({
                            id: 'tiny-mce-multimedia-error',
                            defaultMessage: 'Multimedia (Images, Video, Audio) cannot be pasted or dropped directly. Please use the "Upload Media" button to add media.'
                        });

                        function showMediaError() {
                            editor.notificationManager.open({
                                text: errorText,
                                type: 'error',
                                timeout: 10000
                            });
                        }

                        editor.on('paste', (e) => {
                            const clipboard = e.clipboardData || window.clipboardData;
                            if (!clipboard) return;

                            let hasMedia = false;
                            const mediaTypeRegex = /^(image|video|audio)\//i;

                            for (let i = 0; i < clipboard.items.length; i++) {
                                if (mediaTypeRegex.test(clipboard.items[i].type)) {
                                    hasMedia = true;
                                    break;
                                }
                            }

                            if (hasMedia) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.stopImmediatePropagation();
                                showMediaError();
                                return false;
                            }
                        });

                        editor.on('drop', (e) => {
                            const dataTransfer = e.dataTransfer;
                            if (!dataTransfer) return;

                            let hasMedia = false;

                            if (dataTransfer.files && dataTransfer.files.length > 0) {
                                const mediaTypeRegex = /^(image|video|audio)\//i;
                                for (let i = 0; i < dataTransfer.files.length; i++) {
                                    if (mediaTypeRegex.test(dataTransfer.files[i].type)) {
                                        hasMedia = true;
                                        break;
                                    }
                                }
                            }
                            else {
                                const htmlContent = dataTransfer.getData('text/html');
                                if (htmlContent) {
                                    const mediaTagRegex = /<(img|video|audio|iframe|embed|object|source)/i;

                                    if (mediaTagRegex.test(htmlContent)) {
                                        hasMedia = true;
                                    }
                                }
                            }

                            if (hasMedia) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.stopImmediatePropagation();
                                showMediaError();
                                return false;
                            }
                        });

                        editor.ui.registry.addIcon('open-single-quote', '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" transform="translate(24, 2) scale(-1,1)" d="M10 5c-1.1 0-2 .9-2 2v3c0 1.1.9 2 2 2h1l-1 3h2l1-3V7c0-1.1-.9-2-2-2z"/></svg>');
                        editor.ui.registry.addIcon('close-single-quote', '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" transform="translate(0, 2)" d="M10 5c-1.1 0-2 .9-2 2v3c0 1.1.9 2 2 2h1l-1 3h2l1-3V7c0-1.1-.9-2-2-2z"/></svg>');

                        editor.ui.registry.addButton('addColonsButton', {
                            icon: 'quote',
                            tooltip: 'Insert double quotes',
                            onAction: () => editor.insertContent("“”")
                        });

                        editor.ui.registry.addButton('addOpenColonButton', {
                            icon: 'open-single-quote',
                            tooltip: 'Insert opening single quote',
                            onAction: () => editor.insertContent("‘")
                        });

                        editor.ui.registry.addButton('addCloseColonButton', {
                            icon: 'close-single-quote',
                            tooltip: 'Insert closing single quote',
                            onAction: () => editor.insertContent("’")
                        });

                        editor.on('PastePreProcess', (e) => {
                            e.content = stripNonLatin1(e.content);
                        });

                        editor.on('input', () => {
                            const html = editor.getContent();
                            const cleaned = stripNonLatin1(html);

                            if (html !== cleaned) {
                                const bookmark = editor.selection.getBookmark(2);
                                editor.setContent(cleaned);
                                editor.selection.moveToBookmark(bookmark);
                            }
                        });
                    },
                    menubar: false,
                    paste_data_images: true,
                    // Wiris's docs recommend these so formulas aren't stripped from the content:
                    extended_valid_elements: '*[.*]',
                    draggable_modal: true,
                    formats: {
                        underline: { inline: 'u' }
                    },
                    audio_template_callback: function (d) {
                        return '<audio controls preload="none">\n<source src="' + d.source1 + '"' + (d.source1mime ? ' type="' + d.source1mime + '"' : '') + ' />\n</audio>';
                    },
                    height: this.props.size
                }}
                onEditorChange={this.handleEditorChange}
            />
        );
    }
}

export default injectIntl(TinyMCE);